import React, { useMemo } from "react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import {
  PALETTE,
  fmtMoney,
  fmtDays,
  daysFromMoney,
} from "@/lib/chart-utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

/**
 * TaxBreakdownDonut — Donut showing IR, Cotisations, TVA, Other taxes.
 * Each segment displays UM amount AND days-of-life equivalent.
 */
export default function TaxBreakdownDonut({ ind, currency = "UM" }) {
  const data = useMemo(() => {
    if (!ind) return [];
    const s = ind.salaire_journalier;
    return [
      {
        key: "ir",
        name: "Impôt sur le revenu",
        value: ind.ir_annuel || 0,
        days: daysFromMoney(ind.ir_annuel, s),
        color: PALETTE.red,
      },
      {
        key: "social",
        name: "Cotisations sociales",
        value: ind.cotisations_annuelles || 0,
        days: daysFromMoney(ind.cotisations_annuelles, s),
        color: PALETTE.blue,
      },
      {
        key: "vat",
        name: "TVA",
        value: ind.tva_annuelle || 0,
        days: daysFromMoney(ind.tva_annuelle, s),
        color: PALETTE.gold,
      },
      {
        key: "other",
        name: "Autres taxes",
        value: ind.inputs?.other_taxes || 0,
        days: daysFromMoney(ind.inputs?.other_taxes, s),
        color: PALETTE.slate,
      },
    ].filter((d) => d.value > 0);
  }, [ind]);

  const total = data.reduce((acc, d) => acc + d.value, 0);

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.[0]) return null;
    const p = payload[0].payload;
    const pct = total > 0 ? (p.value / total) * 100 : 0;
    return (
      <div
        className="rounded-md border border-border bg-card p-3 text-xs shadow-lg"
        data-testid="tooltip-donut"
      >
        <div className="font-semibold" style={{ color: p.color }}>
          {p.name}
        </div>
        <div className="mt-1 flex justify-between gap-4">
          <span className="text-muted-foreground">Montant</span>
          <span className="font-mono">{fmtMoney(p.value, currency)}</span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-muted-foreground">% du total</span>
          <span className="font-mono">{pct.toFixed(1)} %</span>
        </div>
        <div className="flex justify-between gap-4 border-t border-border pt-1 mt-1">
          <span className="text-muted-foreground">Vies</span>
          <span className="font-mono font-bold">{fmtDays(p.days)}</span>
        </div>
      </div>
    );
  };

  if (data.length === 0) {
    return (
      <Card data-testid="chart-donut-empty">
        <CardHeader>
          <CardTitle className="text-base">Anatomie des prélèvements</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          Aucun prélèvement à décomposer pour ce profil.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="chart-donut">
      <CardHeader>
        <CardTitle className="text-base">
          Anatomie des prélèvements
          <span className="ml-2 text-xs font-normal text-muted-foreground">
            (annuel)
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-64 w-full" data-testid="chart-donut-container">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius="55%"
                outerRadius="85%"
                paddingAngle={2}
                stroke="var(--background)"
              >
                {data.map((entry) => (
                  <Cell key={entry.key} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend
                verticalAlign="bottom"
                height={24}
                iconType="circle"
                wrapperStyle={{ fontSize: 11 }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-2 flex flex-wrap justify-between gap-2 border-t border-border pt-2 text-xs">
          <span className="text-muted-foreground">Total prélèvements</span>
          <span className="font-mono font-bold">
            {fmtMoney(total, currency)}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
